import { createContext, useCallback, useContext, useState } from 'react';

import { useApiClient } from '../providers/auth-provider';

import type { A2UIComponent, AgentPanelTabState, PanelUpdatePayload } from '@shiftagent/shared';

export interface UseAgentPanelsReturn {
  /** Ordered list of panel tabs (insertion order). */
  tabs: AgentPanelTabState[];
  activeTabId: string | null;
  isPanelOpen: boolean;
  setActiveTab: (tabId: string) => void;
  /** Create or replace a tab by tab_id. Auto-opens the panel on first update. */
  updateTab: (payload: PanelUpdatePayload) => void;
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  /** Hydrate tabs from the API on conversation load/reload. */
  fetchAgentPanels: (conversationId: string) => Promise<void>;
}

export function useAgentPanels(): UseAgentPanelsReturn {
  const apiClient = useApiClient();
  // Map keyed by tab_id preserving insertion order (Map iterates in insertion order)
  const [tabMap, setTabMap] = useState<Map<string, AgentPanelTabState>>(new Map());
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const tabs: AgentPanelTabState[] = Array.from(tabMap.values());

  const setActiveTab = useCallback((tabId: string) => {
    setActiveTabId(tabId);
  }, []);

  const updateTab = useCallback((payload: PanelUpdatePayload) => {
    setTabMap((prev) => {
      const next = new Map(prev);
      const existing = next.get(payload.tab_id);
      next.set(payload.tab_id, {
        tab_id: payload.tab_id,
        title: payload.title,
        components: payload.components as A2UIComponent[],
        updatedAt: Date.now(),
        // Preserve createdAt logic: new tab gets current time; existing keeps prior order
        ...(existing ? {} : {}),
      });
      return next;
    });

    // Auto-set active tab if none selected or if this is the first tab
    setActiveTabId((prev) => prev ?? payload.tab_id);

    // Auto-open panel on first update
    setIsPanelOpen(true);
  }, []);

  const openPanel = useCallback(() => setIsPanelOpen(true), []);
  const closePanel = useCallback(() => setIsPanelOpen(false), []);
  const togglePanel = useCallback(() => setIsPanelOpen((v) => !v), []);

  const fetchAgentPanels = useCallback(
    async (conversationId: string) => {
      // Always reset state when switching conversations
      setTabMap(new Map());
      setActiveTabId(null);
      setIsPanelOpen(false);

      if (!apiClient) return;
      try {
        const panels = await apiClient.listAgentPanels(conversationId);
        if (panels.length === 0) return;
        setTabMap(() => {
          const next = new Map<string, AgentPanelTabState>();
          for (const p of panels) {
            next.set(p.tab_id, {
              tab_id: p.tab_id,
              title: p.title,
              components: p.components as A2UIComponent[],
              updatedAt: new Date(p.updated_at).getTime(),
            });
          }
          return next;
        });
        setActiveTabId(panels[0]?.tab_id ?? null);
        // Don't auto-open on reload — let the user toggle
      } catch {
        // Fetch failure is non-fatal — panels are decorative, not mission-critical
      }
    },
    [apiClient],
  );

  return {
    tabs,
    activeTabId,
    isPanelOpen,
    setActiveTab,
    updateTab,
    openPanel,
    closePanel,
    togglePanel,
    fetchAgentPanels,
  };
}

/**
 * Context that makes the agent panels hook available to both AgentPanel (View)
 * and AgentPanelStreamAdapter (inside StreamRenderer), which live at different
 * points in the component tree but both need to share the same hook instance.
 */
export const AgentPanelsContext = createContext<UseAgentPanelsReturn | null>(null);

export function useAgentPanelsContext(): UseAgentPanelsReturn {
  const ctx = useContext(AgentPanelsContext);
  if (!ctx) throw new Error('useAgentPanelsContext must be used inside AgentPanelsProvider');
  return ctx;
}
